import styled, { css } from 'styled-components/macro';
import modularScale from 'styles/helpers/modular-scale';
import { LinkWithSearchParams as Link } from 'components/base/Link/Link';
import { darken } from 'polished';
import { definitionLabel } from 'styles/base-classes';

// This has disabled stylelinting because the styled stylelint processor cannot
// handle inferred attributes. It doesn't really seem to be supported by
// styled either, it should probably be refactored at some point

/* stylelint-disable property-no-unknown */
const ToolbarDirectionBorder = ({ theme, borderPosition }: any): any => css`
  border-${borderPosition}: 1px solid ${theme.Grey};
  padding-${borderPosition}: ${theme.spacing.medium};
  margin-${borderPosition}: ${theme.spacing.large};

  @media screen and (min-width: 44em) {
    padding-${borderPosition}: ${theme.spacing.xlarge};
    margin-${borderPosition}: ${theme.spacing.xlarge};
  }
`;
/* stylelint-enable property-no-unknown */

export const Toolbar = styled('div')<any>`
  display: flex;
  justify-content: ${({ openFromSide }): string =>
    openFromSide === undefined ? 'space-between' : 'flex-end'};
  align-items: flex-start;
  ${({ openFromSide, borderPosition }): any =>
    openFromSide === undefined && borderPosition && ToolbarDirectionBorder};
`;

export const Main = styled.div<any>`
  @media screen and (min-width: 44em) {
    min-height: ${({ modal }: any): any => (modal ? '50vh' : 0)};
  }

  /** Make sure the first and last element in the Markdown renderer don't have margins */
  > div > * {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  img {
    display: inline-block;
    margin: ${({ theme }): any => theme.spacing.medium} 0;
  }

  p,
  ul,
  ol,
  table td {
    margin-top: 0;
    margin-bottom: ${({ theme }): any => theme.spacing.small};
  }

  a {
    font-weight: 600;
  }

  ul,
  ol {
    margin-left: 0;
    margin-right: 0;
    padding-left: ${({ theme }): any => theme.spacing.xxlarge};

    li {
      padding-left: 0;
    }

    img {
      vertical-align: middle;
    }
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  table {
    width: 100%;
    border-spacing: 0;

    tbody tr:nth-child(odd) {
      background-color: ${({ theme }) => theme.Sand};
    }

    td {
      border-top: 1px solid ${({ theme }) => theme.Grey};
    }

    th {
      ${definitionLabel};
    }

    td,
    th {
      padding: ${({ theme }): any => theme.spacing.xsmall};
      border-left: 1px solid ${({ theme }): any => theme.Grey};

      &:first-child {
        border-left: 0;
      }
    }
  }
`;

export const Section = styled('section')`
  position: relative;
  background: white;
  padding: ${({ theme }): any =>
    `${theme.spacing.large} ${theme.spacing.small}`};
  overflow: auto;
  color: ${({ theme }): any => theme.NightBlue};
  flex: 1 1 auto;

  @media screen and (min-width: 30em) {
    padding: ${({ theme }): any =>
      `${theme.spacing.large} ${theme.spacing.xlarge}`};
  }

  @media screen and (min-width: 44em) {
    padding: ${({ theme }): any =>
      `${theme.spacing.xlarge} ${theme.spacing.huge}`};
  }
`;

export const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.DeepBlue};
  font-weight: 600;
  transition: ${({ theme }) =>
    `${theme.transitionDuration} ${theme.timingFunction} all`};

  > svg {
    margin-right: 0.75em;
    color: ${({ theme }) => theme.AccentGreen};
  }
  &:focus,
  &:active {
    background: ${({ theme }) => theme.Sand};
    color: ${({ theme }) => theme.AccentGreen};
  }
  &:hover {
    color: ${({ theme }) => theme.AccentGreen};
  }
`;

export const CloseButton = styled('button')`
  color: ${({ theme }) => theme.CasperGrey};
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.AccentGreen};
  }
  &:focus,
  &:active {
    color: ${({ theme }) => darken(0.05, theme.AccentGreen)};
  }
`;

export const GlossaryTitle = styled('h1')`
  font-size: ${modularScale(4)};

  @media screen and (min-width: 30em) {
    font-size: ${modularScale(6)};
  }
`;

export const GlossaryTermTitle = styled('h1')`
  font-size: ${modularScale(3)};

  @media screen and (min-width: 30em) {
    font-size: ${modularScale(5)};
  }
`;

export const GlossarySubTitle = styled('h2')`
  ${definitionLabel};

  @media screen and (min-width: 30em) {
    flex: 0 1 40%;
    margin-top: 0.3125rem;
  }
`;

export const GlossaryTermList = styled('ul')`
  @media screen and (min-width: 30em) {
    flex: 0 1 60%;
  }
`;

export const GlossaryTopic = styled('p')`
  ${definitionLabel};
  margin: 0 0 ${modularScale(-6)};
`;

export const GlossaryListItem = styled('li')`
  border-top: 1px solid ${({ theme }) => theme.Grey};
  padding: ${({ theme }) => `${theme.spacing.small} 0`};

  @media screen and (min-width: 30em) {
    display: flex;
    padding: ${({ theme }) => `${theme.spacing.xlarge} 0`};
  }
`;

export const GlossaryTermListItem = styled('li')`
  margin: ${({ theme }) => `${theme.spacing.small} 0`};

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

export const GlossaryTermListLink = styled(Link)`
  color: inherit;
  transition: ${({ theme }): string =>
    ` ${theme.transitionDuration} ${theme.timingFunction}`};
  &:hover {
    color: ${({ theme }): string => theme.AccentGreen};
  }
  &:focus,
  &:active {
    color: ${({ theme }) => darken(0.05, theme.AccentGreen)};
  }
`;

export const Wrapper = styled('div')<any>`
  margin: 0 auto;
  max-width: ${({ modal }: any): string => (modal ? 'inherit' : '35.5em')};
`;

export const TableWrapper = styled('div')`
  overflow-x: auto;
  border: 1px solid ${({ theme }): string => theme.Grey};
  margin: ${({ theme }): string => theme.spacing.xlarge} 0;
`;

export const LoadingBarWrapper = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 3px;
`;

export const EditField = styled('input')`
  width: 100%;
  font: inherit;
  border: 1px solid ${({ theme }): string => theme.NightBlue};
  margin-bottom: 1em;
  padding: ${({ theme }): string => theme.spacing.xsmall};
  resize: vertical;
`;

export const DimmedText = styled('p')`
  color: ${({ theme }): string => theme.CasperGrey};
`;

export const EditModeWrapperAdmin = styled('div')`
  flex: 1 0 100%;
  text-align: right;
`;

export const EditModeButtonAdmin = styled('button')`
  appearance: none;
  color: ${({ theme }): string => theme.White};
  background-color: ${({ theme }): string => theme.AccentGreen};
  font-weight: 600;
  font-size: ${modularScale(-2)};
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.medium}`};
  margin: 0.5em 0;
  border: 0;
  text-transform: uppercase;
  &:not(:first-child) {
    margin-left: 1em;
  }
`;

export const GlossaryNoResults = styled('p')`
  font-size: ${modularScale(1)};
  text-align: center;
  font-weight: 600;
  margin: 1.5em 0;

  @media screen and (min-width: 30em) {
    font-size: ${modularScale(2)};
  }
`;
